import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Star, Upload, Trash2, FileText, Download, RefreshCw, Camera } from "lucide-react";

// ============================================================
// Avatar controls — upload / MS pull / remove.
// Usage: <AvatarControls user={u} onUpdated={callback} canEdit />
// ============================================================
export function AvatarControls({ user, canEdit, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  if (!user) return null;

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please pick an image file");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/users/${user.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated");
      onUpdated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const pullFromMS = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/photo/from-ms`);
      toast.success("Pulled photo from Microsoft 365");
      onUpdated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm("Remove this profile photo?")) return;
    setBusy(true);
    try {
      await api.delete(`/users/${user.id}/photo`);
      toast.success("Photo removed");
      onUpdated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (!canEdit) return null;
  return (
    <div className="flex items-center gap-2" data-testid="avatar-controls">
      <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="avatar-file-input" />
      <button data-testid="avatar-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}
        className="text-[11px] flex items-center gap-1 px-2 py-1 border border-zinc-300 rounded-sm hover:bg-zinc-50 disabled:opacity-50">
        <Camera className="w-3 h-3" /> Upload
      </button>
      <button data-testid="avatar-ms-pull-btn" disabled={busy} onClick={pullFromMS}
        className="text-[11px] flex items-center gap-1 px-2 py-1 border border-zinc-300 rounded-sm hover:bg-zinc-50 disabled:opacity-50">
        <RefreshCw className="w-3 h-3" /> Pull from Microsoft
      </button>
      {(user.profile_photo_url || user.photo_storage) && (
        <button data-testid="avatar-remove-btn" disabled={busy} onClick={remove}
          className="text-[11px] flex items-center gap-1 px-2 py-1 border border-zinc-300 rounded-sm hover:bg-red-50 hover:text-red-700 disabled:opacity-50">
          <Trash2 className="w-3 h-3" /> Remove
        </button>
      )}
    </div>
  );
}


// ============================================================
// Star rating widget (1..5)
// ============================================================
export function StarRating({ value = 0, onChange, readOnly = false, size = 4 }) {
  const cls = `w-${size} h-${size}`;
  return (
    <div className="flex gap-0.5" data-testid={readOnly ? "star-rating-readonly" : "star-rating"}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          data-testid={`star-${n}`}
          disabled={readOnly}
          onClick={() => !readOnly && onChange?.(n)}
          className={`${readOnly ? "cursor-default" : "cursor-pointer hover:scale-110"} transition`}
        >
          <Star className={`${cls} ${n <= value ? "fill-amber-400 text-amber-400" : "text-zinc-300"}`} />
        </button>
      ))}
    </div>
  );
}


// ============================================================
// Skills Matrix section — list & manage the employee's skills.
// ============================================================
export function SkillsSection({ userId, canEdit }) {
  const [skills, setSkills] = useState([]);
  const [editing, setEditing] = useState(null); // { id?, name, rating, category, notes }
  const [uploadingFor, setUploadingFor] = useState(null); // skill id
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get(`/users/${userId}/skills`); setSkills(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const save = async () => {
    if (!editing?.name?.trim()) return toast.error("Skill name is required");
    const payload = {
      name: editing.name.trim(),
      rating: editing.rating || 1,
      category: editing.category || "",
      notes: editing.notes || "",
    };
    try {
      if (editing.id) await api.put(`/skills/${editing.id}`, payload);
      else await api.post(`/users/${userId}/skills`, payload);
      toast.success("Skill saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (sid) => {
    if (!confirm("Delete this skill (and its certificates)?")) return;
    try { await api.delete(`/skills/${sid}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadCert = async (e, skillId) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("label", f.name);
    try {
      await api.post(`/skills/${skillId}/certificates`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Certificate uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingFor(null); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteCert = async (skillId, certId) => {
    if (!confirm("Remove this certificate?")) return;
    try { await api.delete(`/skills/${skillId}/certificates/${certId}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadCert = (skillId, certId) => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    window.open(`${backend}/api/skills/${skillId}/certificates/${certId}`, "_blank");
  };

  return (
    <div data-testid="skills-section" className="border border-zinc-200 rounded-sm p-4 md:col-span-2">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Skills matrix</div>
          <div className="text-[11px] text-zinc-400">Rate each skill 1–5 and attach certificates or degrees.</div>
        </div>
        {canEdit && (
          <button data-testid="add-skill-btn" onClick={() => setEditing({ name: "", rating: 3, category: "", notes: "" })}
            className="text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-sm hover:bg-zinc-800">
            + Add skill
          </button>
        )}
      </div>
      {skills.length === 0 ? (
        <div className="text-sm text-zinc-400">No skills added yet.</div>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {skills.map((s) => (
            <li key={s.id} data-testid={`skill-row-${s.id}`} className="py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{s.name}</span>
                    {s.category && <span className="text-[10px] uppercase text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded-sm">{s.category}</span>}
                  </div>
                  <div className="mt-1"><StarRating value={s.rating} readOnly /></div>
                  {s.notes && <div className="text-xs text-zinc-500 mt-1">{s.notes}</div>}
                  {s.certificates?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {s.certificates.map((c) => (
                        <div key={c.id} data-testid={`cert-${c.id}`} className="inline-flex items-center gap-1 border border-zinc-200 rounded-sm px-1.5 py-0.5 text-[11px] bg-white">
                          <FileText className="w-3 h-3 text-zinc-500" />
                          <button onClick={() => downloadCert(s.id, c.id)} className="hover:underline max-w-[180px] truncate" title={c.label || c.original_filename}>
                            {c.label || c.original_filename}
                          </button>
                          {canEdit && (
                            <button data-testid={`cert-delete-${c.id}`} onClick={() => deleteCert(s.id, c.id)} className="text-red-500 hover:text-red-700 ml-0.5">×</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {canEdit && (
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <div className="flex gap-1">
                      <button data-testid={`skill-edit-${s.id}`} onClick={() => setEditing({ ...s })} className="text-[11px] px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50">Edit</button>
                      <button data-testid={`skill-delete-${s.id}`} onClick={() => remove(s.id)} className="text-[11px] px-2 py-1 border border-zinc-200 rounded-sm hover:bg-red-50 hover:text-red-700">Delete</button>
                    </div>
                    <label className="text-[11px] flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50 cursor-pointer">
                      <Upload className="w-3 h-3" /> Cert
                      <input type="file" className="hidden" data-testid={`cert-upload-${s.id}`}
                        onChange={(e) => { setUploadingFor(s.id); uploadCert(e, s.id); }} />
                    </label>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <SkillEditorModal
          value={editing}
          onChange={setEditing}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function SkillEditorModal({ value, onChange, onSave, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4" onClick={onCancel} data-testid="skill-editor-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-[95vw] max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 text-sm font-semibold">
          {value.id ? "Edit skill" : "Add skill"}
        </div>
        <div className="p-5 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Skill</div>
            <input data-testid="skill-name-input" value={value.name || ""}
              onChange={(e) => onChange({ ...value, name: e.target.value })}
              placeholder="e.g. Advanced Excel"
              className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Rating</div>
            <StarRating value={value.rating || 0} onChange={(r) => onChange({ ...value, rating: r })} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Category (optional)</div>
            <input data-testid="skill-category-input" value={value.category || ""}
              onChange={(e) => onChange({ ...value, category: e.target.value })}
              placeholder="e.g. Software, Language, Compliance"
              className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Notes (optional)</div>
            <textarea data-testid="skill-notes-input" value={value.notes || ""}
              onChange={(e) => onChange({ ...value, notes: e.target.value })}
              rows={2} className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onCancel} className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
            <button data-testid="skill-save-btn" onClick={onSave}
              className="text-white px-4 py-2 rounded-sm text-sm font-medium hover:opacity-90"
              style={{ background: "var(--brand-primary)" }}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// My Documents section — Admin/Manager can upload files (warnings etc.).
// ============================================================
export function DocumentsSection({ userId, canManage }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: "", category: "warning", visible_to_employee: true });
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get(`/users/${userId}/documents`); setDocs(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!form.name.trim()) { toast.error("Give the document a name first"); if (fileRef.current) fileRef.current.value = ""; return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("name", form.name.trim());
    fd.append("category", form.category);
    fd.append("visible_to_employee", String(form.visible_to_employee));
    setUploading(true);
    try {
      await api.post(`/users/${userId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setForm({ name: "", category: "warning", visible_to_employee: true });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => {
    if (!confirm("Delete this document?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const download = (id) => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    window.open(`${backend}/api/documents/${id}/download`, "_blank");
  };

  return (
    <div data-testid="documents-section" className="border border-zinc-200 rounded-sm p-4 md:col-span-2">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">My Documents</div>
          <div className="text-[11px] text-zinc-400">HR files, warnings, contracts and other named documents.</div>
        </div>
      </div>

      {canManage && (
        <div className="mb-4 grid grid-cols-1 md:grid-cols-4 gap-2 bg-zinc-50 border border-zinc-200 p-3 rounded-sm">
          <input data-testid="doc-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Document name (e.g. Written Warning – Attendance)"
            className="col-span-2 border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white" />
          <select data-testid="doc-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="border border-zinc-300 px-3 py-2 rounded-sm text-sm bg-white">
            <option value="warning">Warning</option>
            <option value="contract">Contract</option>
            <option value="review">Review</option>
            <option value="training">Training</option>
            <option value="medical">Medical</option>
            <option value="other">Other</option>
          </select>
          <label className={`flex items-center justify-center gap-2 border border-zinc-300 rounded-sm cursor-pointer text-sm bg-white hover:bg-zinc-50 ${uploading ? "opacity-50" : ""}`}>
            <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Choose file"}
            <input ref={fileRef} type="file" className="hidden" data-testid="doc-file-input" onChange={upload} disabled={uploading} />
          </label>
          <label className="col-span-1 md:col-span-4 flex items-center gap-2 text-[11px] text-zinc-600">
            <input type="checkbox" data-testid="doc-visible-checkbox"
              checked={form.visible_to_employee} onChange={(e) => setForm({ ...form, visible_to_employee: e.target.checked })} />
            Visible to the employee on their own profile
          </label>
        </div>
      )}

      {docs.length === 0 ? (
        <div className="text-sm text-zinc-400">No documents on file.</div>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {docs.map((d) => (
            <li key={d.id} data-testid={`doc-row-${d.id}`} className="py-2 flex items-center gap-3">
              <FileText className="w-4 h-4 text-zinc-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{d.name}</div>
                <div className="text-[11px] text-zinc-500 flex gap-2 flex-wrap">
                  <span className="uppercase">{d.category}</span>
                  <span>·</span>
                  <span>{(d.uploaded_at || "").split("T")[0]}</span>
                  <span>·</span>
                  <span>by {d.uploaded_by_name}</span>
                  {!d.visible_to_employee && <><span>·</span><span className="text-amber-700">private</span></>}
                </div>
              </div>
              <button data-testid={`doc-download-${d.id}`} onClick={() => download(d.id)} className="text-[11px] flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-sm hover:bg-zinc-50">
                <Download className="w-3 h-3" /> Open
              </button>
              {canManage && (
                <button data-testid={`doc-delete-${d.id}`} onClick={() => remove(d.id)} className="text-[11px] flex items-center gap-1 px-2 py-1 border border-zinc-200 rounded-sm hover:bg-red-50 hover:text-red-700">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ============================================================
// BU-level aggregate Skills Matrix — rows: employees, cols: skills.
// ============================================================
export function BUSkillsMatrix({ buId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await api.get(`/business-units/${buId}/skills-matrix`); setData(data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [buId]);

  if (err) return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm p-3">{err}</div>;
  if (!data) return <div className="text-sm text-zinc-500">Loading skills matrix…</div>;

  return (
    <div data-testid="bu-skills-matrix">
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Skills matrix</div>
        <div className="text-xs text-zinc-500">
          Consolidated view of every skill logged by team members in this Business Unit.
          Ratings are 1 (novice) → 5 (expert). Empty cells mean the employee has not logged that skill.
        </div>
      </div>
      {data.skills.length === 0 ? (
        <div className="text-sm text-zinc-500 border border-dashed border-zinc-200 rounded-sm p-10 text-center">
          No skills logged yet in this Business Unit. Employees or managers can add skills from an employee&apos;s tile.
        </div>
      ) : (
        <div className="overflow-auto border border-zinc-200 rounded-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 border-b border-zinc-200 sticky left-0 bg-zinc-50 z-10 min-w-[220px]">Employee</th>
                {data.skills.map((s) => (
                  <th key={s} className="text-center px-3 py-2 border-b border-zinc-200 text-[11px] font-medium whitespace-nowrap">{s}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.user_id} className="odd:bg-white even:bg-zinc-50/50">
                  <td className="px-3 py-2 border-b border-zinc-100 sticky left-0 bg-inherit">
                    <div className="text-sm font-medium">{r.name}</div>
                    <div className="text-[11px] text-zinc-500">{r.title || "—"}</div>
                  </td>
                  {data.skills.map((s) => {
                    const rating = r.ratings?.[s.toLowerCase()];
                    return (
                      <td key={s} className="px-3 py-2 border-b border-zinc-100 text-center">
                        {rating ? (
                          <div className="inline-flex items-center gap-0.5" data-testid={`matrix-${r.user_id}-${s}`}>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <Star key={n} className={`w-3 h-3 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-zinc-200"}`} />
                            ))}
                          </div>
                        ) : (
                          <span className="text-zinc-300">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ============================================================
// Contact block — email + phone display with links.
// ============================================================
export function ContactBlock({ email, phone }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-4 md:col-span-2" data-testid="contact-block">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Contact</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
        <div>
          <div className="text-[11px] uppercase text-zinc-400">Email</div>
          {email ? <a href={`mailto:${email}`} className="text-blue-700 hover:underline break-all">{email}</a> : <span className="text-zinc-400">—</span>}
        </div>
        <div>
          <div className="text-[11px] uppercase text-zinc-400">Phone</div>
          {phone ? <a href={`tel:${phone}`} className="text-blue-700 hover:underline">{phone}</a> : <span className="text-zinc-400">—</span>}
        </div>
      </div>
    </div>
  );
}
